import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Repeat, DollarSign, Clock, CheckCircle, XCircle, AlertTriangle,
  User, Package, Loader2, Send, X, Check, ArrowRight, Shield
} from 'lucide-react';
import { api, useAuth, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-yellow-500', bg: 'bg-yellow-500/10 border-yellow-500/20', icon: Clock },
  countered: { label: 'Countered', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', icon: Repeat },
  accepted: { label: 'Awaiting Confirmation', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: Check },
  confirmed: { label: 'Confirmed', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/20', icon: XCircle },
  expired: { label: 'Expired', color: 'text-zinc-500', bg: 'bg-zinc-500/10 border-zinc-500/20', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'text-zinc-500', bg: 'bg-zinc-500/10 border-zinc-500/20', icon: X },
};

function ListingMini({ listing }) {
  if (!listing) return null;
  const img = listing.images?.[0];
  const imgSrc = img?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
  return (
    <Link to={`/listing/${listing.id}`} className="flex items-center gap-3 p-2 hover:bg-surface-elevated transition-colors">
      <div className="w-12 h-12 bg-surface-elevated flex-shrink-0 overflow-hidden">
        {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-zinc-600 m-3" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{listing.title}</p>
        <p className="text-xs text-gold font-clash">${listing.price?.toLocaleString()}</p>
      </div>
    </Link>
  );
}

function TimelineEntry({ entry, isLast }) {
  const actionLabels = {
    proposed: 'Proposed trade',
    countered: 'Counter-offered',
    accepted: 'Accepted terms',
    confirmed: 'Trade finalized',
    rejected: 'Rejected trade',
    cancelled: 'Cancelled trade',
  };
  const actionColors = {
    proposed: 'bg-blue-500',
    countered: 'bg-yellow-500',
    accepted: 'bg-emerald-500',
    confirmed: 'bg-emerald-600',
    rejected: 'bg-red-500',
    cancelled: 'bg-zinc-500',
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full ${actionColors[entry.action] || 'bg-zinc-500'} flex-shrink-0 mt-1.5`} />
        {!isLast && <div className="w-px flex-1 bg-border-subtle mt-1" />}
      </div>
      <div className="pb-5 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-white font-medium">{entry.by_user_name}</p>
          <p className="text-sm text-zinc-400">{actionLabels[entry.action] || entry.action}</p>
        </div>
        {entry.offered_listings?.length > 0 && (
          <div className="mt-2 space-y-1">
            {entry.offered_listings.map(l => (
              <p key={l.id} className="text-xs text-zinc-400">
                {l.title} (${l.price?.toLocaleString()})
              </p>
            ))}
          </div>
        )}
        {entry.cash_amount > 0 && (
          <p className="text-xs text-gold mt-1">+ ${entry.cash_amount.toLocaleString()} cash</p>
        )}
        {entry.message && (
          <p className="text-xs text-zinc-500 mt-1 italic">"{entry.message}"</p>
        )}
        <p className="text-xs text-zinc-600 mt-1">
          {new Date(entry.timestamp).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default function TradeDetailPanel({ tradeId, onBack }) {
  const { user } = useAuth();
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCounter, setShowCounter] = useState(false);
  const [counterListings, setCounterListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [counterCash, setCounterCash] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [loadingMyListings, setLoadingMyListings] = useState(false);

  const fetchTrade = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trades/${tradeId}`);
      setTrade(data);
    } catch {
      toast.error('Failed to load trade details');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [tradeId, onBack]);

  useEffect(() => {
    fetchTrade();
  }, [fetchTrade]);

  const fetchMyListings = async () => {
    setLoadingMyListings(true);
    try {
      const { data } = await api.get('/my-listings?status=active');
      setMyListings(data);
    } catch {
      // silently handled
    } finally {
      setLoadingMyListings(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      await api.post(`/trades/${tradeId}/${action}`);
      toast.success(action === 'accept' ? 'Trade accepted!' : action === 'reject' ? 'Trade rejected' : 'Trade cancelled');
      await fetchTrade();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCounter = async (e) => {
    e.preventDefault();
    if (counterListings.length === 0 && !counterCash) {
      toast.error('Select items or add cash');
      return;
    }
    setActionLoading('counter');
    try {
      await api.post(`/trades/${tradeId}/counter`, {
        offered_listing_ids: counterListings,
        cash_amount: counterCash ? parseFloat(counterCash) : 0,
        message: counterMessage || null
      });
      toast.success('Counter-offer sent!');
      setShowCounter(false);
      await fetchTrade();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  const openCounter = () => {
    setShowCounter(true);
    setCounterListings([]);
    setCounterCash('');
    setCounterMessage('');
    fetchMyListings();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!trade) return null;

  const isProposer = user?.id === trade.proposer_id;
  const isReceiver = user?.id === trade.receiver_id;
  const otherParty = isProposer ? trade.receiver : trade.proposer;
  const statusCfg = STATUS_CONFIG[trade.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;

  const canAct = ['pending', 'countered', 'accepted'].includes(trade.status);
  const myConfirmed = isProposer ? trade.proposer_confirmed : trade.receiver_confirmed;
  const otherConfirmed = isProposer ? trade.receiver_confirmed : trade.proposer_confirmed;
  const isMyTurn = trade.status === 'pending' ? isReceiver
    : trade.status === 'countered' ? trade.last_offer_by !== user?.id
    : trade.status === 'accepted' ? !myConfirmed
    : false;

  return (
    <div className="space-y-6" data-testid="trade-detail-panel">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 hover:bg-surface-elevated transition-colors" data-testid="trade-back-btn">
          <ArrowLeft className="w-5 h-5 text-zinc-400" />
        </button>
        <div className="flex-1">
          <h3 className="font-clash text-lg font-medium text-white">Trade Details</h3>
          <p className="text-sm text-zinc-400">with {otherParty?.name || 'Unknown'}</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 border ${statusCfg.bg}`}>
          <StatusIcon className={`w-4 h-4 ${statusCfg.color}`} />
          <span className={`text-sm font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
        </div>
      </div>

      {/* Warning for active trades */}
      {canAct && (
        <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-200">
            Review all item details, photos, condition, and seller profile before accepting. Confirmed trades are final.
          </p>
        </div>
      )}

      {/* Current Terms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Target listing (what proposer wants) */}
        <div className="p-4 bg-surface-card border border-border-subtle">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-1">
            <Package className="w-3 h-3" /> {isProposer ? 'You want' : 'Your item'}
          </p>
          <ListingMini listing={trade.target_listing} />
        </div>

        {/* Offered items */}
        <div className="p-4 bg-surface-card border border-border-subtle">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-1">
            <Repeat className="w-3 h-3" /> {isProposer ? 'You offer' : 'They offer'}
          </p>
          {trade.offered_listings?.length > 0 ? (
            <div className="space-y-1">
              {trade.offered_listings.map(l => <ListingMini key={l.id} listing={l} />)}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 py-2">No items offered</p>
          )}
          {trade.cash_amount > 0 && (
            <div className="mt-2 pt-2 border-t border-border-subtle flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gold" />
              <span className="text-gold font-clash font-semibold">${trade.cash_amount.toLocaleString()} cash</span>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation status for accepted trades */}
      {trade.status === 'accepted' && (
        <div className="p-4 bg-surface-card border border-emerald-500/20">
          <p className="text-sm text-white font-medium mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" /> Confirmation Status
          </p>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              {trade.proposer_confirmed ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-yellow-500" />}
              <span className="text-sm text-zinc-300">{trade.proposer?.name || 'Proposer'}</span>
            </div>
            <div className="flex items-center gap-2">
              {trade.receiver_confirmed ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-yellow-500" />}
              <span className="text-sm text-zinc-300">{trade.receiver?.name || 'Receiver'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Expiration */}
      {canAct && trade.expires_at && (
        <p className="text-xs text-zinc-500 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Expires: {new Date(trade.expires_at).toLocaleString()}
        </p>
      )}

      {/* Actions */}
      {canAct && (
        <div className="space-y-3">
          {isMyTurn && trade.status !== 'accepted' && (
            <div className="flex gap-3">
              <button
                onClick={() => handleAction('accept')}
                disabled={actionLoading !== null}
                className="btn-gold flex-1 py-3 flex items-center justify-center gap-2"
                data-testid="trade-accept-btn"
              >
                {actionLoading === 'accept' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Accept
              </button>
              <button
                onClick={openCounter}
                disabled={actionLoading !== null}
                className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2"
                data-testid="trade-counter-btn"
              >
                <Repeat className="w-4 h-4" /> Counter
              </button>
              <button
                onClick={() => handleAction('reject')}
                disabled={actionLoading !== null}
                className="btn-secondary py-3 px-4 hover:border-red-500 hover:text-red-500"
                data-testid="trade-reject-btn"
              >
                {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              </button>
            </div>
          )}

          {trade.status === 'accepted' && !myConfirmed && (
            <button
              onClick={() => handleAction('accept')}
              disabled={actionLoading !== null}
              className="btn-gold w-full py-3 flex items-center justify-center gap-2"
              data-testid="trade-confirm-btn"
            >
              {actionLoading === 'accept' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Confirm Trade
            </button>
          )}

          {trade.status === 'accepted' && myConfirmed && !otherConfirmed && (
            <div className="p-3 bg-surface-elevated text-center">
              <p className="text-sm text-zinc-400">Waiting for {otherParty?.name} to confirm...</p>
            </div>
          )}

          {!isMyTurn && trade.status !== 'accepted' && (
            <div className="p-3 bg-surface-elevated text-center">
              <p className="text-sm text-zinc-400">Waiting for {otherParty?.name} to respond...</p>
            </div>
          )}

          <button
            onClick={() => handleAction('cancel')}
            disabled={actionLoading !== null}
            className="w-full py-2 text-sm text-zinc-500 hover:text-red-500 transition-colors"
            data-testid="trade-cancel-btn"
          >
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Trade'}
          </button>
        </div>
      )}

      {/* Counter-offer form */}
      {showCounter && (
        <form onSubmit={handleCounter} className="p-4 bg-surface-card border border-gold/20 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-clash text-base font-medium text-white">Counter-Offer</h4>
            <button type="button" onClick={() => setShowCounter(false)} className="text-zinc-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {loadingMyListings ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-gold animate-spin" /></div>
          ) : (
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {myListings.map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setCounterListings(prev => prev.includes(item.id) ? prev.filter(x => x !== item.id) : [...prev, item.id])}
                  className={`w-full p-2 flex items-center gap-3 text-left border transition-colors ${
                    counterListings.includes(item.id) ? 'bg-gold/10 border-gold' : 'bg-surface-base border-border-subtle'
                  }`}
                >
                  <div className="w-8 h-8 bg-surface-elevated flex-shrink-0 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.images[0]}` : item.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : <Package className="w-4 h-4 text-zinc-600 m-2" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{item.title}</p>
                    <p className="text-xs text-zinc-400">${item.price?.toLocaleString()}</p>
                  </div>
                  {counterListings.includes(item.id) && <Check className="w-4 h-4 text-gold" />}
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
            <input type="number" value={counterCash} onChange={e => setCounterCash(e.target.value)}
              placeholder="Cash amount (optional)" className="input-dark w-full pl-7 pr-4 py-2 text-sm" min="0" step="0.01" />
          </div>

          <textarea value={counterMessage} onChange={e => setCounterMessage(e.target.value)}
            placeholder="Message..." className="input-dark w-full px-3 py-2 h-16 resize-none text-sm" />

          <button type="submit" disabled={actionLoading === 'counter' || (counterListings.length === 0 && !counterCash)}
            className="btn-gold w-full py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {actionLoading === 'counter' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Counter-Offer
          </button>
        </form>
      )}

      {/* Trade History Timeline */}
      <div className="pt-4 border-t border-border-subtle">
        <h4 className="font-clash text-base font-medium text-white mb-4">Trade History</h4>
        <div className="space-y-0">
          {trade.history?.map((entry, idx) => (
            <TimelineEntry key={entry.timestamp} entry={entry} isLast={idx === trade.history.length - 1} />
          ))}
        </div>
      </div>
    </div>
  );
}
